import { useRef, useState, type ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  Camera,
  ChevronRight,
  DoorOpen,
  Ruler,
  Store as StoreIcon,
  Palette,
  Wand2,
  Sparkles,
  ShoppingBag,
  Bell,
  Shield,
  Settings,
  HelpCircle,
  LogOut,
  Image as ImageIcon,
  type LucideIcon,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, Modal, Spinner } from '@/components/ui';
import { PhotoCropper } from '@/components/PhotoCropper';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { readAsDataUrl } from '@/lib/image';
import { formatCurrency } from '@/lib/utils';
import type { DashboardStats, Fit, Closet } from '@/lib/types';

interface MenuItem {
  icon: LucideIcon;
  label: string;
  href: string;
  description: string;
}

const MENU_SECTIONS: { title: string; items: MenuItem[] }[] = [
  {
    title: 'Account',
    items: [
      { icon: User, label: 'Account Details', href: '/profile/account', description: 'Username, email, password' },
      { icon: DoorOpen, label: 'Closets', href: '/profile/closets', description: 'Manage your closet locations' },
      { icon: Ruler, label: 'My Sizes', href: '/profile/sizes', description: 'Save your measurements' },
      { icon: StoreIcon, label: 'My Stores', href: '/profile/stores', description: 'Stores, SAs, and spending' },
      { icon: Palette, label: 'My Designers', href: '/profile/designers', description: 'Brands, sizing, and items' },
      { icon: Wand2, label: 'Style Profile', href: '/profile/style', description: 'Your AI taste fingerprint' },
      { icon: Sparkles, label: 'Service Providers', href: '/profile/services', description: 'Cleaners, tailors, and more' },
    ],
  },
  {
    title: 'Archive',
    items: [
      { icon: ShoppingBag, label: 'Wishlist Archive', href: '/profile/archive/wishlist', description: 'View removed wishlist items' },
    ],
  },
  {
    title: 'Preferences',
    items: [
      { icon: Bell, label: 'Notifications', href: '/profile/notifications', description: 'Manage alerts and reminders' },
      { icon: Shield, label: 'Privacy', href: '/profile/privacy', description: 'Data and sharing settings' },
      { icon: Settings, label: 'App Settings', href: '/profile/settings', description: 'Display, theme, language' },
    ],
  },
  {
    title: 'Support',
    items: [
      { icon: HelpCircle, label: 'Help & FAQ', href: '/profile/help', description: 'Get answers and support' },
    ],
  },
];

export function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [showImagePicker, setShowImagePicker] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const { data: stats } = useApi<DashboardStats>('/dashboard/stats');
  const { data: fitsRes } = useApi<{ fits: Fit[] }>('/fits');
  const { data: closetsRes } = useApi<{ closets: Closet[] }>('/closets');

  const itemCount = stats?.totalItems ?? 0;
  const fitCount = fitsRes?.fits?.length ?? 0;
  const closetCount = closetsRes?.closets?.length ?? 0;

  const handleImageSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !user) return;
    // Open the crop/zoom editor first so the user can frame the avatar.
    const dataUrl = await readAsDataUrl(file);
    setShowImagePicker(false);
    setCropSrc(dataUrl);
  };

  const handleCropApply = async (image: string) => {
    if (!user) return;
    setUploading(true);
    try {
      await api.post('/photos', { ownerType: 'user', ownerId: user.id, image });
      await refreshUser();
      setCropSrc(null);
    } catch {
      /* ignore */
    } finally {
      setUploading(false);
    }
  };

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Page>
      <PageHeader section="Profile" />

      <Main className="space-y-6">
        {/* Profile Card */}
        <Card className="p-6">
          <div className="flex flex-col items-center">
            <button onClick={() => setShowImagePicker(true)} className="relative group">
              <div className="w-24 h-24 rounded-full bg-secondary border-2 border-border overflow-hidden">
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-12 h-12 text-muted-foreground" />
                  </div>
                )}
              </div>
              <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <div className="absolute bottom-0 right-0 p-1.5 bg-primary rounded-full border-2 border-card">
                <Camera className="w-3.5 h-3.5 text-primary-foreground" />
              </div>
            </button>

            <h2 className="mt-4 text-lg font-semibold text-foreground">
              {user?.displayName || user?.username || 'You'}
            </h2>
            {user?.username && <p className="text-sm text-muted-foreground">@{user.username}</p>}

            <div className="flex gap-6 mt-5 pt-5 border-t border-border w-full justify-center">
              <Stat value={itemCount} label="Items" />
              <Stat value={fitCount} label="Fits" />
              <Stat value={closetCount} label="Closets" />
              <Stat value={formatCurrency(stats?.totalValue ?? 0)} label="Value" />
            </div>
          </div>
        </Card>

        {/* Menu Sections */}
        {MENU_SECTIONS.map((section) => (
          <div key={section.title} className="space-y-2">
            <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide px-1 font-display">
              {section.title}
            </h3>
            <Card className="overflow-hidden divide-y divide-border">
              {section.items.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  className="flex items-center gap-4 p-4 hover:bg-secondary/50 transition-colors"
                >
                  <div className="p-2.5 bg-secondary rounded-xl">
                    <item.icon className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{item.label}</p>
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </Link>
              ))}
            </Card>
          </div>
        ))}

        <button
          onClick={handleSignOut}
          className="w-full flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-2xl text-destructive hover:bg-destructive/5 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span className="text-sm font-medium">Sign Out</span>
        </button>
      </Main>

      <Modal isOpen={showImagePicker} onClose={() => setShowImagePicker(false)} title="Change Profile Photo">
        <div className="space-y-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full flex items-center gap-4 p-4 bg-secondary rounded-xl hover:bg-secondary/70 transition-colors disabled:opacity-50"
          >
            <Camera className="w-5 h-5 text-foreground" />
            <span className="text-sm font-medium text-foreground">Take Photo</span>
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full flex items-center gap-4 p-4 bg-secondary rounded-xl hover:bg-secondary/70 transition-colors disabled:opacity-50"
          >
            <ImageIcon className="w-5 h-5 text-foreground" />
            <span className="text-sm font-medium text-foreground">Choose from Library</span>
          </button>
          {uploading && (
            <div className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
              <Spinner className="w-4 h-4" />
              Uploading…
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            className="hidden"
          />
        </div>
      </Modal>

      {cropSrc && (
        <PhotoCropper
          image={cropSrc}
          onCancel={() => (uploading ? undefined : setCropSrc(null))}
          onApply={handleCropApply}
        />
      )}
    </Page>
  );
}

function Stat({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="text-center">
      <p className="text-xl font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
